/**
 * The minimap: the whole settlement on two hundred pixels, bottom right.
 *
 * Every game this one is trying to feel like has one, and for the same reason.
 * The map is 384 tiles a side and the camera shows forty of them at a working
 * zoom, so a player standing at their Hearth can see about one percent of the
 * board. Everything outside that — which gate the horde is walking in through,
 * where the wall is thin, which quarter the woodcutters have stripped bare — is
 * knowable only by flying the camera there and back. That is not a decision
 * being made under pressure; it is a search.
 *
 * Three layers, drawn on three different clocks, because they change on three
 * different clocks:
 *
 *   • **Ground and walls** — baked once into an offscreen 384×384 canvas, one
 *     pixel per tile, and rebaked only when `terrainVersion` or `wallVersion`
 *     moves. A road laid or a curtain breached is a handful of events a minute;
 *     repainting 147,456 tiles sixty times a second for them would cost more
 *     than the rest of the HUD put together.
 *   • **Buildings and units** — redrawn on a fixed 15 Hz tick into a second
 *     offscreen canvas. A soldier crosses one minimap pixel every two seconds
 *     at a sprint, so sixty samples a second buys nothing an eye can see, and a
 *     siege puts two thousand of them on the board.
 *   • **The camera frame** — every frame, straight onto the visible canvas. It
 *     is the one mark tied to the player's own hand, and a viewport rectangle
 *     that lags a pan by 60ms reads as the whole map being made of treacle.
 *
 * Ground colours come from the active biome so snow, grass and forest agree
 * with the main view. Actor outlines keep orders readable on light ground.
 */

import { MAP_H, MAP_W } from '../sim/constants.ts';
import { Alliance, allianceOf, UF } from '../sim/types.ts';
import type { World } from '../sim/world.ts';
import { C, toCss } from '../data/palette.ts';
import type { Camera } from '../render/camera.ts';
import { surfaceTable, themeFor } from '../render/biome.ts';

/** Side of the chart in CSS pixels. Square, because the map is. */
const SIZE = 232;

/** Seconds between actor repaints. */
const ACTOR_PERIOD = 1 / 15;

/** Stone on the chart: a curtain has to read as a line, so it is nearly bone. */
const WALL = '#9a8f79';
const GATE = '#d8b862';

export interface MinimapCallbacks {
  /** The player pointed at a place. Put the camera there. */
  onSeek(wx: number, wy: number): void;
}

export class Minimap {
  readonly el: HTMLElement;
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;

  /** The baked ground. One pixel per tile, so no scaling maths anywhere. */
  private ground = document.createElement('canvas');
  private groundCtx: CanvasRenderingContext2D;
  /** Ground is rebaked when the biome, terrain, walls or world change. */
  private groundKey = '';
  private groundWorld: WeakRef<World> | null = null;

  /** The actors, on their own clock. */
  private actors = document.createElement('canvas');
  private actorsCtx: CanvasRenderingContext2D;
  private actorTimer = ACTOR_PERIOD;

  /** Device pixels per CSS pixel, resampled on each resize. */
  private dpr = 1;
  /** The pointer is down on the chart and every move is a seek. */
  private dragging = false;
  /** Wall-clock seconds, for the pulse on a threatened gate. */
  private clock = 0;

  constructor(
    root: HTMLElement,
    private cb: MinimapCallbacks,
  ) {
    this.el = document.createElement('div');
    this.el.className = 'panel';
    this.el.id = 'minimap';

    const hdr = document.createElement('div');
    hdr.className = 'hdr';
    hdr.textContent = 'The Ground';
    this.el.appendChild(hdr);

    this.canvas = document.createElement('canvas');
    this.canvas.id = 'minimap-cv';
    this.el.appendChild(this.canvas);
    root.appendChild(this.el);

    this.ctx = this.canvas.getContext('2d') as CanvasRenderingContext2D;
    this.ground.width = MAP_W;
    this.ground.height = MAP_H;
    this.groundCtx = this.ground.getContext('2d') as CanvasRenderingContext2D;
    this.actors.width = MAP_W;
    this.actors.height = MAP_H;
    this.actorsCtx = this.actors.getContext('2d') as CanvasRenderingContext2D;

    this.resize();

    /**
     * `mousedown` and a window-level `mousemove`, not a listener on the canvas.
     *
     * Dragging the view around by the chart is the gesture this exists for, and
     * a drag that dies the instant the pointer crosses the panel's edge is a
     * drag that dies about a third of a second in. The release is on the window
     * for the same reason: a button let go somewhere else is still let go.
     */
    this.canvas.addEventListener('mousedown', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      this.dragging = true;
      this.seek(ev);
    });
    window.addEventListener('mousemove', (ev) => {
      if (this.dragging) this.seek(ev);
    });
    window.addEventListener('mouseup', () => {
      this.dragging = false;
    });
    // The canvas sits over the world; a click here must never also place a
    // building underneath it.
    this.canvas.addEventListener('contextmenu', (ev) => ev.preventDefault());
  }

  /**
   * Show or hide the whole chart — there is nothing to chart on the realm.
   *
   * A **class**, not an inline `display`. On a narrow screen the chart is one of
   * the bottom sheets and the stylesheet decides whether it is up; an inline
   * style beats a class, so writing one here pinned the chart open across the
   * middle of a phone screen with no tab able to shut it — and, because it also
   * swallowed every touch, made the map unreachable and the whole game
   * untestable on a phone. `!important` because this rule is not a layout
   * opinion competing with the sheet rules, it is "this panel does not apply at
   * all right now", and it has to win wherever it is asked.
   */
  setVisible(on: boolean): void {
    this.el.classList.toggle('hidden', !on);
  }

  /**
   * Match the backing store to the display size, once and on each DPR change.
   *
   * The guard compares against the size we *want*, not against zero. A fresh
   * `<canvas>` is 300×150 with no CSS size at all, so "is it non-empty yet"
   * answers yes on the very first call and the chart spends the whole run being
   * a squashed 300×150 rectangle overflowing the bottom of the screen.
   */
  private resize(): void {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const want = Math.round(SIZE * dpr);
    if (dpr === this.dpr && this.canvas.width === want) return;
    this.dpr = dpr;
    this.canvas.width = want;
    this.canvas.height = want;
    this.canvas.style.width = `${SIZE}px`;
    this.canvas.style.height = `${SIZE}px`;
  }

  private seek(ev: MouseEvent): void {
    const r = this.canvas.getBoundingClientRect();
    const fx = (ev.clientX - r.left) / r.width;
    const fy = (ev.clientY - r.top) / r.height;
    this.cb.onSeek(
      Math.max(0, Math.min(MAP_W, fx * MAP_W)),
      Math.max(0, Math.min(MAP_H, fy * MAP_H)),
    );
  }

  update(w: World, cam: Camera, dt: number): void {
    this.resize();
    this.clock += dt;
    this.bakeGround(w);

    this.actorTimer += dt;
    if (this.actorTimer >= ACTOR_PERIOD) {
      this.actorTimer = 0;
      this.drawActors(w);
    }

    const ctx = this.ctx;
    const px = this.canvas.width;
    ctx.clearRect(0, 0, px, px);
    // Nearest-neighbour on the way down would alias a one-tile road into
    // existence and out of it as the chart is redrawn; smoothing turns 384 tiles
    // into 232 pixels the way a map is meant to shrink.
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(this.ground, 0, 0, px, px);
    ctx.drawImage(this.actors, 0, 0, px, px);

    this.drawGates(w, ctx, px);
    this.drawCamera(cam, ctx, px);
  }

  /**
   * The ground and the stone, baked.
   *
   * Written through `ImageData` rather than 147,456 `fillRect` calls: one pass
   * over three typed arrays and one upload, which is a few milliseconds on the
   * handful of frames a minute that need it.
   */
  private bakeGround(w: World): void {
    const key = `${w.config.biome ?? 'plain'}:${w.map.terrainVersion}:${w.map.wallVersion}`;
    if (key === this.groundKey && this.groundWorld?.deref() === w) return;
    this.groundKey = key;
    this.groundWorld = new WeakRef(w);

    const img = this.groundCtx.createImageData(MAP_W, MAP_H);
    const d = img.data;
    const terrain = w.map.terrain;
    const wall = w.map.wall;

    // The colour table, unpacked to bytes once, so the inner loop is three
    // array reads and four writes rather than a string parse per tile.
    const rgb = new Uint8Array(3 * 16);
    const surfaces = surfaceTable(themeFor(w.config.biome));
    for (const k of Object.keys(surfaces)) {
      const t = Number(k);
      const color = surfaces[t].base;
      rgb[t * 3] = (color >> 16) & 255;
      rgb[t * 3 + 1] = (color >> 8) & 255;
      rgb[t * 3 + 2] = color & 255;
    }
    const wr = parseInt(WALL.slice(1, 3), 16);
    const wg = parseInt(WALL.slice(3, 5), 16);
    const wb = parseInt(WALL.slice(5, 7), 16);

    for (let i = 0, o = 0; i < terrain.length; i++, o += 4) {
      if (wall[i] !== 0) {
        d[o] = wr;
        d[o + 1] = wg;
        d[o + 2] = wb;
        d[o + 3] = 255;
        continue;
      }
      const t = terrain[i];
      d[o] = rgb[t * 3];
      d[o + 1] = rgb[t * 3 + 1];
      d[o + 2] = rgb[t * 3 + 2];
      d[o + 3] = 255;
    }
    this.groundCtx.putImageData(img, 0, 0);
  }

  /**
   * Buildings, then units, then the Hearth on top of both.
   *
   * Order is the whole of the legibility here: a building is a static two-tile
   * block and a man is a moving dot, and the thing the player is looking for in
   * a siege is always the man. The Hearth goes last because if it is under
   * anything at all the chart has lost its one fixed point.
   */
  private drawActors(w: World): void {
    const ctx = this.actorsCtx;
    ctx.clearRect(0, 0, MAP_W, MAP_H);

    // Buildings. Under construction reads hollow, so "what have I actually got"
    // and "what have I promised myself" are not the same mark.
    const bs = w.buildings;
    for (let i = 0; i < bs.slots.high; i++) {
      if (bs.slots.alive[i] === 0) continue;
      const home = allianceOf(bs.team[i]) === Alliance.Home;
      ctx.fillStyle = bs.build[i] < 1 ? 'rgba(200,164,76,0.35)' : home ? '#b99a52' : '#7d6a86';
      ctx.fillRect(bs.tx[i] - 1, bs.ty[i] - 1, 3, 3);
    }

    /**
     * Units, in two passes so the ones that matter are never buried.
     *
     * Villagers outnumber soldiers ten to one in a working town, and they are
     * the least interesting thing on the chart — a chart that is mostly a
     * porridge of workers is one the eye stops checking. They are drawn first,
     * small and dim; everything armed is drawn over them larger and brighter.
     *
     * **Every size here is in tiles, and the chart is resampled 384 → 232 on
     * the way to the screen.** A one-tile mark therefore arrives as six tenths
     * of a pixel and is blended into the ground until it is gone, which is what
     * the first cut of this drew every villager and every soldier as.
     */
    const u = w.units;
    const high = u.slots.high;
    for (let pass = 0; pass < 2; pass++) {
      for (let i = 0; i < high; i++) {
        if (u.slots.alive[i] === 0) continue;
        const villager = (u.flags[i] & UF.Villager) !== 0;
        if (villager !== (pass === 0)) continue;
        const foe = allianceOf(u.team[i]) === Alliance.Foe;
        if (villager) {
          ctx.fillStyle = 'rgba(163,150,124,0.85)';
          ctx.fillRect((u.x[i] | 0) - 1, (u.y[i] | 0) - 1, 2, 2);
          continue;
        }
        // Champions get twice the width. They are the thing a siege is decided
        // by and they are one unit in two thousand.
        const big = (u.flags[i] & (UF.Champion | UF.Large)) !== 0;
        const s = big ? 6 : 3;
        // A dark casing keeps both armies visible against white snow.
        ctx.fillStyle = '#18232a';
        ctx.fillRect((u.x[i] | 0) - (s >> 1) - 1, (u.y[i] | 0) - (s >> 1) - 1, s + 2, s + 2);
        ctx.fillStyle = foe ? toCss(C.hostileRim) : toCss(C.friendlyRim);
        ctx.fillRect((u.x[i] | 0) - (s >> 1), (u.y[i] | 0) - (s >> 1), s, s);
      }
    }

    // The Hearth. A ring rather than a dot, so it reads as a place and not as
    // one more champion.
    ctx.strokeStyle = toCss(C.ember);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(w.map.hearthX, w.map.hearthY, 5, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = toCss(C.emberLight);
    ctx.fillRect(w.map.hearthX - 1, w.map.hearthY - 1, 3, 3);
  }

  /**
   * The gates, and which of them the scouts say they are coming through.
   *
   * This is the one piece of information the settlement HUD already had and
   * could not point at: the scouting panel names a gate in words — "the Drowned
   * Mile (east)" — and the player then has to work out which edge of a
   * three-hundred-tile map that is. Here it is a mark on the edge that pulses.
   */
  private drawGates(w: World, ctx: CanvasRenderingContext2D, px: number): void {
    const s = px / MAP_W;
    const incoming = new Set(w.incomingGates);
    for (let i = 0; i < w.map.gates.length; i++) {
      const g = w.map.gates[i];
      const hot = incoming.has(i);
      const x = g.x * s;
      const y = g.y * s;
      // Held inside the frame: a gate is on the map edge, so a mark centred on
      // it is half outside the canvas and reads as clipped rather than as a
      // gate.
      const cx = Math.max(6, Math.min(px - 6, x));
      const cy = Math.max(6, Math.min(px - 6, y));
      if (hot) {
        // Two seconds a breath, and the pulse is on the ring rather than the
        // fill so it cannot be mistaken for the gate moving.
        const t = 0.5 + 0.5 * Math.sin(this.clock * Math.PI);
        ctx.strokeStyle = `rgba(156,51,54,${(0.45 + 0.55 * t).toFixed(3)})`;
        ctx.lineWidth = 2 * this.dpr;
        ctx.beginPath();
        ctx.arc(cx, cy, (4 + 3 * t) * this.dpr, 0, Math.PI * 2);
        ctx.stroke();
      }
      ctx.fillStyle = hot ? toCss(C.oxbloodLight) : GATE;
      const r = (hot ? 3 : 2) * this.dpr;
      ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
    }
  }

  /** What the player is actually looking at, as a frame on the chart. */
  private drawCamera(cam: Camera, ctx: CanvasRenderingContext2D, px: number): void {
    const s = px / MAP_W;
    const b = cam.viewBounds(0);
    const x = b.x0 * s;
    const y = b.y0 * s;
    const wpx = (b.x1 - b.x0) * s;
    const hpx = (b.y1 - b.y0) * s;
    ctx.strokeStyle = 'rgba(230,207,146,0.85)';
    ctx.lineWidth = Math.max(1, this.dpr);
    ctx.strokeRect(x, y, wpx, hpx);
    // A second, darker line outside the first: the frame has to be findable
    // over pale stone as well as over dark ash, and one colour cannot do both.
    ctx.strokeStyle = 'rgba(0,0,0,0.55)';
    ctx.strokeRect(x - this.dpr, y - this.dpr, wpx + this.dpr * 2, hpx + this.dpr * 2);
  }
}
